import React, { useState } from 'react';
import {
  Button,
  Input,
  Select,
  Form,
  Space,
  Card,
  Switch,
  InputNumber,
  Popconfirm,
  Modal,
  Checkbox,
  Divider,
  message
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { CoffeeEquipmentField } from '../types/coffeeEquipment';

const { TextArea } = Input;
const { Option } = Select;

interface CoffeeEquipmentFieldsManagerProps {
  fields: CoffeeEquipmentField[];
  onChange: (fields: CoffeeEquipmentField[]) => void;
}

export const CoffeeEquipmentFieldsManager: React.FC<CoffeeEquipmentFieldsManagerProps> = ({
  fields,
  onChange
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingField, setEditingField] = useState<CoffeeEquipmentField | null>(null);
  const [form] = Form.useForm();

  const fieldTypes = [
    { value: 'text', label: 'Văn bản' },
    { value: 'textarea', label: 'Văn bản nhiều dòng' },
    { value: 'number', label: 'Số' },
    { value: 'number_range', label: 'Khoảng số' },
    { value: 'date', label: 'Ngày tháng' },
    { value: 'select', label: 'Chọn từ danh sách' },
    { value: 'checkbox', label: 'Hộp tích' },
    { value: 'multi_checkbox', label: 'Nhiều hộp tích' },
    { value: 'radio', label: 'Chọn một' },
    { value: 'range', label: 'Thanh trượt' },
    { value: 'rating', label: 'Đánh giá sao' },
    { value: 'image', label: 'Hình ảnh' },
    { value: 'auto_text', label: 'Mã tự động' },
  ];

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const handleAdd = () => {
    setEditingField(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (field: CoffeeEquipmentField) => {
    setEditingField(field);
    form.setFieldsValue(field);
    setIsModalVisible(true);
  };

  const handleDelete = (fieldId: string) => {
    const newFields = fields.filter(f => f.id !== fieldId);
    onChange(newFields);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const newField: CoffeeEquipmentField = {
        id: editingField?.id || generateId(),
        ...values,
      };

      let newFields;
      if (editingField) {
        newFields = fields.map(f => f.id === editingField.id ? newField : f);
      } else {
        newFields = [...fields, newField];
      }

      onChange(newFields);
      setIsModalVisible(false);
      form.resetFields();
      setEditingField(null);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const renderFieldPreview = (field: CoffeeEquipmentField) => {
    return (
      <Card
        key={field.id}
        size="small"
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{field.name}</span>
            <Space>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEdit(field)}
              />
              <Popconfirm
                title="Xóa trường"
                description="Bạn có chắc chắn muốn xóa trường này?"
                onConfirm={() => handleDelete(field.id)}
                okText="Xóa"
                cancelText="Hủy"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          </div>
        }
        style={{ marginBottom: 8 }}
      >
        <div>
          <strong>Loại:</strong> {fieldTypes.find(t => t.value === field.type)?.label}
          {field.required && <span style={{ color: 'red', marginLeft: 8 }}>*</span>}
        </div>
        {field.placeholder && (
          <div><strong>Placeholder:</strong> {field.placeholder}</div>
        )}
        {field.tooltip && (
          <div><strong>Tooltip:</strong> {field.tooltip}</div>
        )}
        {field.suffix && (
          <div><strong>Đơn vị:</strong> {field.suffix}</div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Trường thông tin</h3>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Thêm trường
        </Button>
      </div>

      <div style={{ maxHeight: 400, overflowY: 'auto' }}>
        {fields.map(renderFieldPreview)}
        {fields.length === 0 && (
          <div style={{ textAlign: 'center', color: '#999', padding: 20 }}>
            Chưa có trường nào. Nhấn "Thêm trường" để bắt đầu.
          </div>
        )}
      </div>

      <Modal
        title={editingField ? 'Sửa trường' : 'Thêm trường mới'}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingField(null);
        }}
        okText={editingField ? 'Cập nhật' : 'Thêm'}
        cancelText="Hủy"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            required: false,
            type: 'text'
          }}
        >
          <Form.Item
            name="name"
            label="Tên trường"
            rules={[{ required: true, message: 'Vui lòng nhập tên trường' }]}
          >
            <Input placeholder="Ví dụ: Tên sản phẩm, Giá bán, Màu sắc..." />
          </Form.Item>

          <Form.Item
            name="type"
            label="Loại dữ liệu"
            rules={[{ required: true, message: 'Vui lòng chọn loại dữ liệu' }]}
          >
            <Select placeholder="Chọn loại dữ liệu">
              {fieldTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="required"
            valuePropName="checked"
          >
            <Checkbox>Bắt buộc</Checkbox>
          </Form.Item>

          <Form.Item
            name="placeholder"
            label="Placeholder"
          >
            <Input placeholder="Văn bản gợi ý cho người dùng" />
          </Form.Item>

          <Form.Item
            name="tooltip"
            label="Tooltip"
          >
            <Input placeholder="Thông tin hướng dẫn cho trường này" />
          </Form.Item>

          <Form.Item
            name="suffix"
            label="Đơn vị"
          >
            <Input placeholder="Ví dụ: VND, kg, cm, %" />
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, currentValues) => 
              prevValues.type !== currentValues.type
            }
          >
            {({ getFieldValue }) => {
              const fieldType = getFieldValue('type');
              
              if (fieldType === 'number' || fieldType === 'number_range' || fieldType === 'range') {
                return (
                  <>
                    <Divider>Cài đặt số</Divider>
                    <Space>
                      <Form.Item
                        name="min"
                        label="Giá trị tối thiểu"
                      >
                        <InputNumber placeholder="0" />
                      </Form.Item>
                      <Form.Item
                        name="max"
                        label="Giá trị tối đa"
                      >
                        <InputNumber placeholder="100" />
                      </Form.Item>
                      <Form.Item
                        name="step"
                        label="Bước nhảy"
                      >
                        <InputNumber placeholder="1" />
                      </Form.Item>
                    </Space>
                  </>
                );
              }

              if (fieldType === 'select' || fieldType === 'radio') {
                return (
                  <>
                    <Divider>Tùy chọn</Divider>
                    <Form.Item
                      name="options"
                      label="Các lựa chọn"
                    >
                      <Select
                        mode="tags"
                        placeholder="Nhập các lựa chọn và nhấn Enter"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </>
                );
              }

              if (fieldType === 'image') {
                return (
                  <>
                    <Divider>Cài đặt hình ảnh</Divider>
                    <Form.Item
                      name="multiple"
                      valuePropName="checked"
                    >
                      <Checkbox>Cho phép chọn nhiều ảnh</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="maxFiles"
                      label="Số ảnh tối đa"
                    >
                      <InputNumber min={1} max={10} placeholder="5" />
                    </Form.Item>
                    <Form.Item
                      name="allowDriveUpload"
                      valuePropName="checked"
                    >
                      <Checkbox>Cho phép upload từ Google Drive</Checkbox>
                    </Form.Item>
                  </>
                );
              }

              if (fieldType === 'auto_text') {
                return (
                  <>
                    <Divider>Cài đặt mã tự động</Divider>
                    <Space>
                      <Form.Item
                        name={['autoConfig', 'prefix']}
                        label="Tiền tố"
                        rules={[{ required: true, message: 'Vui lòng nhập tiền tố' }]}
                      >
                        <Input placeholder="Ví dụ: BR, GR, EQ" />
                      </Form.Item>
                      <Form.Item
                        name={['autoConfig', 'length']}
                        label="Độ dài mã"
                        rules={[{ required: true, message: 'Vui lòng nhập độ dài' }]}
                      >
                        <InputNumber min={3} max={10} placeholder="6" />
                      </Form.Item>
                    </Space>
                  </>
                );
              }

              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
